import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric'
  });
};

const TEMPLATE_ACCENTS = {
  'classic-pink': { accent: '#ec4899', light: '#fdf2f8', line: '#f9a8d4' },
  'starry-night': { accent: '#6366f1', light: '#eef2ff', line: '#a5b4fc' },
  'sunset-glow': { accent: '#f97316', light: '#fff7ed', line: '#fdba74' },
  'ocean-breeze': { accent: '#06b6d4', light: '#ecfeff', line: '#67e8f9' },
  'romance-rose': { accent: '#f43f5e', light: '#fff1f2', line: '#fda4af' },
  'midnight-love': { accent: '#8b5cf6', light: '#f5f3ff', line: '#c4b5fd' },
  'garden-bloom': { accent: '#d946ef', light: '#fdf4ff', line: '#e879f9' },
  'candlelight': { accent: '#d97706', light: '#fffbeb', line: '#fcd34d' },
};

const DEFAULT_ACCENT = { accent: '#ec4899', light: '#fdf2f8', line: '#f9a8d4' };

const PaperLayout = ({ invite, children }) => {
  const templateId = invite?.template_id || '';
  const colors = TEMPLATE_ACCENTS[templateId] || DEFAULT_ACCENT;
  const senderName = invite?.sender_profile?.name || invite?.sender_display_name || 'Someone special';

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2, duration: 0.6, ease: 'easeOut' }}
      className="w-full max-w-sm p-4"
    >
      {/* Paper card */}
      <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">
        {/* Accent top stripe */}
        <div className="h-1.5" style={{ backgroundColor: colors.accent }} />

        {/* Paper texture — subtle horizontal lines */}
        <div className="absolute inset-0 pointer-events-none opacity-[0.04]">
          {[...Array(20)].map((_, i) => (
            <div key={i} className="border-b border-gray-400" style={{ height: '28px' }} />
          ))}
        </div>

        {/* Left margin line */}
        <div className="absolute left-10 top-0 bottom-0 w-px opacity-10" style={{ backgroundColor: colors.accent }} />

        <div className="relative p-6 text-center">
          {/* Sender header */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="flex items-center justify-center gap-3 mb-5"
          >
            <img
              src={invite?.sender_profile?.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(senderName)}&background=ec4899&color=fff`}
              alt={senderName}
              className="w-11 h-11 rounded-full border-2 border-white shadow-md"
            />
            <div className="text-left">
              <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">From</p>
              <p className="font-bold text-gray-800 font-serif">{senderName}</p>
            </div>
          </motion.div>

          {/* Occasion badge */}
          {invite?.occasion && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5 }}
              className="rounded-xl px-4 py-2 mb-5 inline-block"
              style={{ backgroundColor: colors.light }}
            >
              <p className="text-sm font-bold" style={{ color: colors.accent }}>{invite.occasion}</p>
            </motion.div>
          )}

          {/* Divider */}
          <div className="w-16 h-0.5 mx-auto mb-5 rounded-full opacity-30" style={{ backgroundColor: colors.accent }} />

          {/* Photo (couples) */}
          {invite?.photo_url && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.6 }}
              className="mb-5"
            >
              <img src={invite.photo_url} alt="Memory" className="w-full h-44 object-cover rounded-2xl shadow-lg" />
            </motion.div>
          )}

          {/* Message */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="mb-5"
          >
            <p className="text-lg font-serif text-gray-700 italic leading-relaxed whitespace-pre-wrap">
              "{invite?.message || 'You have been invited to something special!'}"
            </p>
          </motion.div>

          {/* Music indicator */}
          {invite?.music_title && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
              className="flex items-center justify-center gap-2 text-xs text-gray-400 mb-4"
            >
              <SafeIcon name="Music" style={{ color: colors.accent }} />
              <span>♪ {invite.music_title}</span>
            </motion.div>
          )}

          {/* Proposed date */}
          {invite?.event_date && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.9 }}
              className="rounded-xl px-4 py-3 mb-4"
              style={{ backgroundColor: colors.light }}
            >
              <p className="text-sm font-semibold flex items-center justify-center gap-1.5" style={{ color: colors.accent }}>
                <SafeIcon name="Calendar" className="w-4 h-4" /> {formatDate(invite.event_date)}
                {invite.event_time && ` · ${invite.event_time}`}
              </p>
              {invite.event_location && (
                <p className="text-xs text-gray-500 mt-1">📍 {invite.event_location}</p>
              )}
            </motion.div>
          )}

          {/* Question (singles) */}
          {invite?.flow_type === 'singles' && invite?.invitation_question && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1 }}
              className="rounded-xl p-4 border"
              style={{ backgroundColor: colors.light, borderColor: colors.line }}
            >
              <p className="text-base font-bold font-serif text-gray-800">{invite.invitation_question}</p>
            </motion.div>
          )}

          {/* Bottom divider */}
          <div className="w-16 h-0.5 mx-auto mt-5 rounded-full opacity-30" style={{ backgroundColor: colors.accent }} />
          <p className="text-[10px] text-gray-300 mt-3 tracking-widest uppercase">DayClap</p>
        </div>
      </div>

      {/* Action buttons below the paper */}
      {children && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2 }}
          className="mt-6"
        >
          {children}
        </motion.div>
      )}
    </motion.div>
  );
};

export default PaperLayout;